import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useState } from 'react';

import { RootState } from 'store/rootReducer';
import { getUpphovsman } from 'store/upphovsmanSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaDialog } from 'raa-react';

import { SpinnerContainer } from 'components/drawerSections/DrawerSections';
import ErrorBoundary from 'components/errorBoundary/ErrorBoundary';
import { ExternalLink } from 'components/sharedElements/sharedElements';

import { Upphovsmanroll, Upphovsorganisation, Upphovsperson } from 'models';

import { isUpphovsorganisation, isUpphovsperson } from 'utilities/typeGuards';

const toUpphovsmanDisplayName = (upphovsman: Upphovsperson | Upphovsorganisation): string => {
  if (isUpphovsperson(upphovsman)) {
    return `${upphovsman.fornamn} ${upphovsman.efternamn}`;
  } else if (isUpphovsorganisation(upphovsman)) {
    return upphovsman.upphovsorganisationsnamn;
  }
  // Is never by this point so this is exhaustive.
  return upphovsman;
};

const RenderDialogContent: FC<{ id: string; setHeader: Dispatch<SetStateAction<string>> }> = ({
  id,
  setHeader
}): ReactElement => {
  const dispatch = useAppDispatch();
  const { data: upphovsman, requestStatus } = useAppSelector((state: RootState) => state.upphovsman);

  useEffect(() => {
    if (upphovsman && requestStatus !== 'pending') {
      setHeader(toUpphovsmanDisplayName(upphovsman.typ));
    } else {
      setHeader('');
    }
  }, [upphovsman, requestStatus, setHeader]);

  useEffect(() => {
    dispatch(getUpphovsman(id));
  }, [id, dispatch]);

  return (
    <div className="tw-min-h-[10rem]">
      <SpinnerContainer showSpinner={requestStatus === 'pending'}>
        {upphovsman && (
          <div className="tw-py-8">
            {isUpphovsperson(upphovsman.typ) && upphovsman.typ.fodelse_ar && (
              <p className="tw-my-2">{`Född ${upphovsman.typ.fodelse_ar}`}</p>
            )}

            {upphovsman.typ.identifieringsstod && <p className="tw-my-2">{upphovsman.typ.identifieringsstod}</p>}

            <h3 className="tw-heading-sm tw-mt-2">Roll</h3>
            <p>
              {upphovsman.roller.length > 0 ? (
                upphovsman.roller.map((upphovsmanroll: Upphovsmanroll) => upphovsmanroll.roll_namn).join(', ')
              ) : (
                <>&ndash;</>
              )}
            </p>

            <h3 className="tw-heading-sm tw-mt-4">Läs mer</h3>
            {(upphovsman.hanvisningar.length > 0 && (
              <ul>
                {upphovsman.hanvisningar.map((hanvisning, index) => (
                  <li key={hanvisning.namn + index}>
                    <ExternalLink text={hanvisning.namn} href={hanvisning.uri} target="_blank" />
                  </li>
                ))}
              </ul>
            )) || <>&ndash;</>}
          </div>
        )}
      </SpinnerContainer>
    </div>
  );
};

const UpphovsmanInfoDialog: FC<{ id: string; unsetDialog: () => void }> = ({ id, unsetDialog }): ReactElement => {
  const [header, setHeader] = useState<string>('');

  return (
    <RaaDialog header={header} close={unsetDialog} ariaDescribedBy="dialog-description">
      <ErrorBoundary>
        <RenderDialogContent id={id} setHeader={setHeader} />
      </ErrorBoundary>
    </RaaDialog>
  );
};

export default UpphovsmanInfoDialog;
